import { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";

const Payment = ({ onTokenReady, values, registerTokenGenerator }) => {
  const [cardNumber, setCardNumber] = useState("");
  const [expMonth, setExpMonth] = useState("");
  const [expYear, setExpYear] = useState("");
  const [cvv, setCvv] = useState("");
  const [cardBrand, setCardBrand] = useState(null);

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // -------------------------------------------------
  // Load Accept.js automatically
  // -------------------------------------------------
  useEffect(() => {
    if (document.getElementById("acceptjs-script")) return;

    const script = document.createElement("script");
    script.id = "acceptjs-script";
    script.src = "https://js.authorize.net/v1/Accept.js";
    script.async = true;

    document.body.appendChild(script);
  }, []);

  // -------------------------------------------------
  // Detect card brand
  // -------------------------------------------------
  const detectBrand = (num) => {
    const n = num.replace(/\s+/g, "");

    if (/^4/.test(n)) return "visa";
    if (/^5[1-5]/.test(n)) return "mastercard";
    if (/^3[47]/.test(n)) return "amex";
    if (/^6(?:011|5)/.test(n)) return "discover";

    return null;
  };

  // -------------------------------------------------
  // Luhn Validation
  // -------------------------------------------------
  const luhnCheck = (value) => {
    const num = value.replace(/\D+/g, "");
    let sum = 0;
    let dbl = false;

    for (let i = num.length - 1; i >= 0; i--) {
      let digit = parseInt(num.charAt(i), 10);

      if (dbl) {
        digit *= 2;
        if (digit > 9) digit -= 9;
      }

      sum += digit;
      dbl = !dbl;
    }

    return sum % 10 === 0;
  };

  // -------------------------------------------------
  // Format card number + detect brand
  // -------------------------------------------------
  const formatCardNumber = (v) => {
    const cleaned = v.replace(/\D+/g, "").slice(0, 16);
    const formatted = cleaned.replace(/(.{4})/g, "$1 ").trim();

    setCardBrand(detectBrand(cleaned));
    return formatted;
  };

  // -------------------------------------------------
  // Real-time validator
  // -------------------------------------------------
  const isFormValid = () => {
    const n = cardNumber.replace(/\s+/g, "");

    if (n.length < 12 || !luhnCheck(n)) return false;
    if (expMonth < 1 || expMonth > 12) return false;
    if (expYear.length !== 4) return false;

    if (cardBrand === "amex" && cvv.length !== 4) return false;
    if (cardBrand !== "amex" && cvv.length !== 3) return false;

    return true;
  };

  // -------------------------------------------------
  // Generate Accept.js Token (STABLE)
  // -------------------------------------------------
  const tryGenerateToken = useCallback(() => {
    if (!isFormValid()) {
      setError("Please complete all card fields correctly.");
      return;
    }

    if (!window.Accept) {
      setError("Payment system is still loading...");
      return;
    }

    setError("");
    setLoading(true);

    const cardData = {
      cardNumber: cardNumber.replace(/\s+/g, ""),
      month: expMonth,
      year: expYear,
      cardCode: cvv,
    };

    const authData = {
      clientKey: import.meta.env.VITE_AUTHORIZE_CLIENT_KEY,
      apiLoginID: import.meta.env.VITE_AUTHORIZE_API_LOGIN_ID,
    };

    const billingAddress = {
      firstName: values.firstName,
      lastName: values.lastName,
      address: values.address,
      city: values.city,
      state: values.state,
      zip: values.zipCode,
      country: "US",
    };

    const secureData = {
      authData,
      cardData,
      billingAddress,
    };

    window.Accept.dispatchData(secureData, (response) => {
      setLoading(false);

      if (response.messages.resultCode === "Error") {
        setError(response.messages.message[0].text);
        return;
      }

      onTokenReady(response.opaqueData);
    });
  }, [cardNumber, expMonth, expYear, cvv, cardBrand, values, onTokenReady]);

  // -------------------------------------------------
  // Register token generator ONCE (no race conditions)
  // -------------------------------------------------
  useEffect(() => {
    if (registerTokenGenerator) {
      registerTokenGenerator(tryGenerateToken);
    }
  }, [registerTokenGenerator, tryGenerateToken]);

  // -------------------------------------------------
  // Card Brand Indicator
  // -------------------------------------------------
  const renderBrandIcon = () => {
    if (!cardBrand) return null;

    return (
      <span className="card-brand" style={{ position: "absolute", right: 10 }}>
        {cardBrand.toUpperCase()}
      </span>
    );
  };

  return (
    <div>
      <h3>Payment Information</h3>

      <div className="form-controls">
        <label>Card Number *</label>
        <div style={{ position: "relative" }}>
          <input
            type="text"
            autoComplete="cc-number"
            inputMode="numeric"
            value={cardNumber}
            onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
            placeholder="4242 4242 4242 4242"
            maxLength={19}
          />
          {renderBrandIcon()}
        </div>
      </div>

      <div className="form-inline">
        <div className="form-controls">
          <label>Exp Month *</label>
          <input
            type="text"
            placeholder="MM"
            autoComplete="cc-exp-month"
            maxLength={2}
            value={expMonth}
            onChange={(e) =>
              setExpMonth(e.target.value.replace(/\D/g, "").slice(0, 2))
            }
          />
        </div>

        <div className="form-controls">
          <label>Exp Year *</label>
          <input
            type="text"
            placeholder="YYYY"
            autoComplete="cc-exp-year"
            maxLength={4}
            value={expYear}
            onChange={(e) =>
              setExpYear(e.target.value.replace(/\D/g, "").slice(0, 4))
            }
          />
        </div>
      </div>

      <div className="form-controls">
        <label>CVV *</label>
        <input
          type="text"
          autoComplete="cc-csc"
          inputMode="numeric"
          maxLength={cardBrand === "amex" ? 4 : 3}
          value={cvv}
          onChange={(e) => setCvv(e.target.value.replace(/\D/g, ""))}
          placeholder={cardBrand === "amex" ? "4 digits" : "3 digits"}
        />
      </div>

      {loading && <p>Securing card…</p>}
      {error && <p className="error">{error}</p>}
    </div>
  );
};

Payment.propTypes = {
  onTokenReady: PropTypes.func.isRequired,
  values: PropTypes.object.isRequired,
  registerTokenGenerator: PropTypes.func.isRequired,
};

export default Payment;

// import { useEffect, useState } from "react";
// import PropTypes from "prop-types";

// const Payment = ({ onTokenReady, values }) => {
//   const [cardNumber, setCardNumber] = useState("");
//   const [expMonth, setExpMonth] = useState("");
//   const [expYear, setExpYear] = useState("");
//   const [cvv, setCvv] = useState("");
//   const [cardBrand, setCardBrand] = useState(null);

//   const [error, setError] = useState("");
//   const [loading, setLoading] = useState(false);

//   // -------------------------------------------------
//   // Load Accept.js automatically
//   // -------------------------------------------------
//   useEffect(() => {
//     if (document.getElementById("acceptjs-script")) return;

//     const script = document.createElement("script");
//     script.id = "acceptjs-script";
//     script.src = "https://js.authorize.net/v1/Accept.js";
//     script.async = true;

//     document.body.appendChild(script);
//   }, []);

//   // -------------------------------------------------
//   // Detect card brand via BIN patterns
//   // -------------------------------------------------
//   const detectBrand = (num) => {
//     const n = num.replace(/\s+/g, "");

//     if (/^4/.test(n)) return "visa";
//     if (/^5[1-5]/.test(n)) return "mastercard";
//     if (/^3[47]/.test(n)) return "amex";
//     if (/^6(?:011|5)/.test(n)) return "discover";

//     return null;
//   };

//   // -------------------------------------------------
//   // Luhn Validation
//   // -------------------------------------------------
//   const luhnCheck = (value) => {
//     const num = value.replace(/\D+/g, "");
//     let sum = 0;
//     let dbl = false;

//     for (let i = num.length - 1; i >= 0; i--) {
//       let digit = parseInt(num.charAt(i));

//       if (dbl) {
//         digit *= 2;
//         if (digit > 9) digit -= 9;
//       }

//       sum += digit;
//       dbl = !dbl;
//     }

//     return sum % 10 === 0;
//   };

//   // -------------------------------------------------
//   // Formatting card number
//   // -------------------------------------------------
//   const formatCardNumber = (v) => {
//     const cleaned = v.replace(/\D+/g, "").slice(0, 16);
//     const formatted = cleaned.replace(/(.{4})/g, "$1 ").trim();

//     const brand = detectBrand(cleaned);
//     setCardBrand(brand);

//     return formatted;
//   };

//   // -------------------------------------------------
//   // Real-time field validator
//   // -------------------------------------------------
//   const isFormValid = () => {
//     const n = cardNumber.replace(/\s+/g, "");
//     if (n.length < 12 || !luhnCheck(n)) return false;
//     if (expMonth < 1 || expMonth > 12) return false;
//     if (expYear.length < 4) return false;

//     if (cardBrand === "amex" && cvv.length !== 4) return false;
//     if (cardBrand !== "amex" && cvv.length !== 3) return false;

//     return true;
//   };

//   // -------------------------------------------------
//   // Generate Accept.js opaqueData Token
//   // -------------------------------------------------
//   const tryGenerateToken = () => {
//     if (!isFormValid()) return;

//     if (!window.Accept) {
//       setError("Payment system is still loading...");
//       return;
//     }

//     setError("");
//     setLoading(true);

//     // -------------------------------
//     // Card data
//     // -------------------------------
//     const cardData = {
//       cardNumber: cardNumber.replace(/\s+/g, ""),
//       month: expMonth,
//       year: expYear,
//       cardCode: cvv,
//     };

//     // -------------------------------
//     // Auth keys
//     // -------------------------------
//     const authData = {
//       clientKey: import.meta.env.VITE_AUTHORIZE_CLIENT_KEY,
//       apiLoginID: import.meta.env.VITE_AUTHORIZE_API_LOGIN_ID,
//     };

//     // -------------------------------
//     // REQUIRED FIX ❗ Billing Address
//     // -------------------------------
//     const billingAddress = {
//       firstName: values.firstName,
//       lastName: values.lastName,
//       address: values.address,
//       city: values.city,
//       state: values.state,
//       zip: values.zipCode,
//       country: "US",
//     };

//     const secureData = {
//       authData,
//       cardData,
//       billingAddress,
//     };

//     window.Accept.dispatchData(secureData, (response) => {
//       setLoading(false);

//       if (response.messages.resultCode === "Error") {
//         setError(response.messages.message[0].text);
//         return;
//       }

//       // send opaqueData up
//       onTokenReady(response.opaqueData);
//     });
//   };

//   // Trigger token generation automatically when valid
//   useEffect(() => {
//     if (isFormValid()) {
//       tryGenerateToken();
//     }
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [cardNumber, expMonth, expYear, cvv]);

//   // -------------------------------------------------
//   // Card Brand Icons (simple text for now)
//   // -------------------------------------------------
//   const renderBrandIcon = () => {
//     if (!cardBrand) return null;

//     return (
//       <span className="card-brand" style={{ position: "absolute", right: 10 }}>
//         {cardBrand.toUpperCase()}
//       </span>
//     );
//   };

//   return (
//     <div>
//       <h3>Payment Information</h3>

//       <div className="form-controls">
//         <label>Card Number *</label>
//         <div style={{ position: "relative" }}>
//           <input
//             type="text"
//             autoComplete="cc-number"
//             inputMode="numeric"
//             value={cardNumber}
//             onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
//             placeholder="4242 4242 4242 4242"
//             maxLength={19}
//           />
//           {renderBrandIcon()}
//         </div>
//       </div>

//       <div className="form-inline">
//         <div className="form-controls">
//           <label>Exp Month *</label>
//           <input
//             type="text"
//             placeholder="MM"
//             autoComplete="cc-exp-month"
//             maxLength={2}
//             value={expMonth}
//             onChange={(e) =>
//               setExpMonth(e.target.value.replace(/\D/g, "").slice(0, 2))
//             }
//           />
//         </div>

//         <div className="form-controls">
//           <label>Exp Year *</label>
//           <input
//             type="text"
//             placeholder="YYYY"
//             autoComplete="cc-exp-year"
//             maxLength={4}
//             value={expYear}
//             onChange={(e) =>
//               setExpYear(e.target.value.replace(/\D/g, "").slice(0, 4))
//             }
//           />
//         </div>
//       </div>

//       <div className="form-controls">
//         <label>CVV *</label>
//         <input
//           type="text"
//           autoComplete="cc-csc"
//           inputMode="numeric"
//           maxLength={cardBrand === "amex" ? 4 : 3}
//           value={cvv}
//           onChange={(e) => setCvv(e.target.value.replace(/\D/g, ""))}
//           placeholder={cardBrand === "amex" ? "4 digits" : "3 digits"}
//         />
//       </div>

//       {loading && <p>Securing card…</p>}
//       {error && <p className="error">{error}</p>}
//     </div>
//   );
// };

// Payment.propTypes = {
//   onTokenReady: PropTypes.func.isRequired,
//   values: PropTypes.object.isRequired, // NEW
// };

// export default Payment;
